import {parseNumber} from '@benchling/js-utils';

import {assert} from 'Asserts';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import StorableSummaryManager from 'models/managers/StorableSummaryManager';
import {
  ColumnDefinition,
  getColumnDefinitionIndex,
  QuantityColumnDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {toSpreadsheetIndex, ValueError, Warning} from 'notebook/tables/excess/Util';
import {CustomResolutionQueryType} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {formatTypedValue, isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {NumberTypedValue, StringTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {TransferTableColumnGroup} from 'notebook/transfer-tables/TransferTableConstants';

export function setCustomEvalForQuantity(
  columnDefinitions: Array<ColumnDefinition>,
  cellValue: Cell,
  rowIndex: number
): Cell {
  const containerColumnIndex = getColumnDefinitionIndex({
    columnDefinitionType: TableColumnType.CONTAINERS,
    tableColumnDefinitions: columnDefinitions,
    columnGroup: TransferTableColumnGroup.SOURCE,
    isOptional: true,
  });
  const plateColumnIndex = getColumnDefinitionIndex({
    columnDefinitionType: TableColumnType.PLATE,
    tableColumnDefinitions: columnDefinitions,
    columnGroup: TransferTableColumnGroup.SOURCE,
    isOptional: true,
  });
  let positionColumnIndex = -1;
  if (plateColumnIndex !== -1) {
    positionColumnIndex = getColumnDefinitionIndex({
      columnDefinitionType: TableColumnType.POSITION,
      tableColumnDefinitions: columnDefinitions,
      columnGroup: TransferTableColumnGroup.SOURCE,
      isOptional: true,
    });
    assert(
      positionColumnIndex !== -1,
      'Position source column is expected if source plate column is present.'
    );
  }

  if (containerColumnIndex === -1 && plateColumnIndex === -1) {
    return {
      ...cellValue,
      customEvalArgs: [],
      customEval: sampleQuantityCustomEval,
    };
  } else if (containerColumnIndex !== -1 && plateColumnIndex === -1) {
    return {
      ...cellValue,
      customEvalArgs: [
        `=${toSpreadsheetIndex({
          row: rowIndex,
          column: containerColumnIndex,
        })}`,
      ],
      customEval: containerQuantityCustomEval,
    };
  } else if (containerColumnIndex === -1 && plateColumnIndex !== -1) {
    return {
      ...cellValue,
      customEvalArgs: [
        `=${toSpreadsheetIndex({
          row: rowIndex,
          column: plateColumnIndex,
        })}`,
        `=${toSpreadsheetIndex({
          row: rowIndex,
          column: positionColumnIndex,
        })}`,
      ],
      customEval: wellPlateQuantityCustomEval,
    };
  } else {
    assert(
      containerColumnIndex !== -1 && plateColumnIndex !== -1,
      'Unexpected container and plate column indexes',
      {containerColumnIndex, plateColumnIndex}
    );
    return {
      ...cellValue,
      customEvalArgs: [
        `=${toSpreadsheetIndex({
          row: rowIndex,
          column: containerColumnIndex,
        })}`,
        `=${toSpreadsheetIndex({
          row: rowIndex,
          column: plateColumnIndex,
        })}`,
        `=${toSpreadsheetIndex({
          row: rowIndex,
          column: positionColumnIndex,
        })}`,
      ],
      customEval: containerOrWellPlateQuantityCustomEval,
    };
  }
}

function getCellWithResolvedNumberValue(quantityTypedValue: TypedValue): {
  val: NumberTypedValue | StringTypedValue;
  error: ValueError | Warning | null;
  locked: false;
} {
  const formattedValue = formatTypedValue(quantityTypedValue);
  const stringResolvedValue: StringTypedValue = {
    type: BaseTypedValueType.STRING,
    rawValue: formattedValue,
  };
  if (formattedValue === '') {
    return {
      val: stringResolvedValue,
      error: null,
      locked: false,
    };
  }

  const rawValue = parseNumber(formattedValue);
  const numberError = new ValueError(`${formattedValue} is not a valid positive number.`);
  if (rawValue == null) {
    return {
      val: stringResolvedValue,
      error: numberError,
      locked: false,
    };
  }

  const resolvedValue: NumberTypedValue = {
    type: BaseTypedValueType.NUMBER,
    rawValue,
    defaultPrecision: null,
  };
  return {
    val: resolvedValue,
    error: rawValue < 0 ? numberError : null,
    locked: false,
  };
}

export function sampleQuantityCustomEval(
  quantityTypedValue: TypedValue,
  quantityColumnDefinition: QuantityColumnDefinition
): {val: NumberTypedValue | StringTypedValue; error: ValueError | Warning | null; locked: false} {
  return getCellWithResolvedNumberValue(quantityTypedValue);
}

export function containerQuantityCustomEval(
  quantityTypedValue: TypedValue,
  columnDefinition: QuantityColumnDefinition,
  [containerValue]: [TypedValue],
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): {val: TypedValue; error: ValueError | Warning | null; locked: false} {
  const cellValue = getCellWithResolvedNumberValue(quantityTypedValue);
  if (cellValue.val.type !== BaseTypedValueType.NUMBER || cellValue.error) {
    return cellValue;
  }
  const resolvedCellValue = cellValue.val;
  let containerId;
  if (containerValue.type === BaseTypedValueType.STORABLES) {
    assert(containerValue.storableSummaries.length === 1, 'Single source container value is expected.');
    containerId = containerValue.storableSummaries[0].id;
  } else if (isTypedValueEmpty(containerValue)) {
    return {val: resolvedCellValue, error: null, locked: false};
  } else {
    assert(false, 'Unexpected typed value type', {typedValue: containerValue});
  }

  if (!containerId) {
    return {val: resolvedCellValue, error: null, locked: false};
  }

  const {resolvedValue, error} = resolveFromResolutionQuery(
    resolvedCellValue,
    {
      customResolutionQueryType:
        CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_QUANTITY_WITH_SOURCE_CONTAINER,
      containerId,
      quantityValue: resolvedCellValue.rawValue,
      quantityUnits: columnDefinition.units,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );

  return {val: resolvedValue, error, locked: false};
}

export function wellPlateQuantityCustomEval(
  quantityTypedValue: TypedValue,
  columnDefinition: QuantityColumnDefinition,
  [plateValue, positionValue]: [TypedValue, TypedValue],
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): {val: TypedValue; error: ValueError | Warning | null; locked: false} {
  const cellValue = getCellWithResolvedNumberValue(quantityTypedValue);
  if (cellValue.val.type !== BaseTypedValueType.NUMBER || cellValue.error) {
    return cellValue;
  }

  const resolvedCellValue = cellValue.val;
  if (isTypedValueEmpty(plateValue)) {
    return {val: resolvedCellValue, error: null, locked: false};
  } else if (plateValue.type !== BaseTypedValueType.STORABLES) {
    throw new Error(`Unexpected typed value type ${plateValue.type}`);
  }

  assert(plateValue.storableSummaries.length === 1, 'Single source plate is expected.');
  const plateId = plateValue.storableSummaries[0].id;

  if (positionValue.type !== BaseTypedValueType.GRID_COORDINATES) {
    throw new Error('Position value is required if plate value is specified');
  }
  const position = formatTypedValue(positionValue);

  const {resolvedValue, error} = resolveFromResolutionQuery(
    resolvedCellValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_QUANTITY_WITH_SOURCE_PLATE,
      plateId,
      position,
      quantityValue: cellValue.val.rawValue,
      quantityUnits: columnDefinition.units,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: false};
}

export function containerOrWellPlateQuantityCustomEval(
  quantityTypedValue: TypedValue,
  quantityColumnDefinition: QuantityColumnDefinition,
  [containerValue, plateValue, positionValue]: [TypedValue, TypedValue, TypedValue],
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): {val: TypedValue; error: ValueError | Warning | null; locked: false} {
  if (plateValue.type === BaseTypedValueType.STORABLES) {
    // If container and plate are both specified, the container value is the plate well's container,
    // so we can use the well plate custom eval.
    return wellPlateQuantityCustomEval(
      quantityTypedValue,
      quantityColumnDefinition,
      [plateValue, positionValue],
      undefined,
      resolveFromResolutionQuery
    );
  } else {
    return containerQuantityCustomEval(
      quantityTypedValue,
      quantityColumnDefinition,
      [containerValue],
      undefined,
      resolveFromResolutionQuery
    );
  }
}
